import { KPI_TYPE_LABELS } from '@constants/enumLabels';
import { KPIDriversDisplay } from '@deckManagement/components/Common/KPIDriversDisplay';
import { EditVariantViewProps } from '@deckManagement/types/KPILibrary.types';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatasetSource } from '@services/api/deckManagement/graphql/schema/API';
import { resolveDatasetSourceLabel } from '@utils/enumLabelHelpers';
import { KpiDimension } from 'types/PageSidePanel.types';

import VariantUsageSummary from './VariantUsageSummary';

/**
 * Edit Variant view content — 3-column layout with variant details, driver overrides, and usage summary.
 */
const EditVariantView = ({
  selectedRootKPI,
  selectedVariant,
  editVariantName,
  onVariantNameChange,
  driverDisplayOptions,
  placeholderDrivers,
  onDriverChange,
  onDriverToggle,
  usageSummary,
  isUsageSummaryLoading,
  expandedDeckId,
  onExpandChange,
  isDialogMode,
  editVariantActions,
}: EditVariantViewProps) => (
  <>
    <Box className='kpi-library-edit-variant-content'>
      {/* Column 1: Variant Details (name, type, etc.) */}
      <Box className='edit-variant-details'>
        <TextField
          fullWidth
          label='Deckline Name'
          value={selectedRootKPI.root_kpi_name}
          margin='dense'
          size='small'
          disabled
        />
        <TextField
          fullWidth
          label='Variant'
          value={editVariantName}
          onChange={(e) => onVariantNameChange(e.target.value)}
          margin='dense'
          size='small'
          autoComplete='off'
        />
        <List dense className='edit-variant-metadata'>
          <ListItem>
            <ListItemText primary='Type' />
            <Typography variant='body2'>{KPI_TYPE_LABELS[selectedVariant.kpi_type] ?? '~'}</Typography>
          </ListItem>
          <ListItem>
            <ListItemText primary='Source' />
            <Typography variant='body2'>{resolveDatasetSourceLabel(selectedVariant.dataset_source)}</Typography>
          </ListItem>
          <ListItem>
            <ListItemText primary='Created By' />
            <Typography variant='body2'>{selectedVariant.created_by ?? '-'}</Typography>
          </ListItem>
          <ListItem>
            <ListItemText primary='Created On' />
            <Typography variant='body2'>
              {selectedVariant.created_date ? new Date(selectedVariant.created_date).toLocaleDateString() : '-'}
            </Typography>
          </ListItem>
          <ListItem>
            <ListItemText primary='Updated By' />
            <Typography variant='body2'>{selectedVariant.last_updated_by ?? '-'}</Typography>
          </ListItem>
          <ListItem>
            <ListItemText primary='Updated On' />
            <Typography variant='body2'>
              {selectedVariant.last_updated_date
                ? new Date(selectedVariant.last_updated_date).toLocaleDateString()
                : '-'}
            </Typography>
          </ListItem>
        </List>
      </Box>
      {/* Column 2: KPI Driver overrides */}
      <Box className='edit-variant-drivers'>
        <KPIDriversDisplay
          datasetSource={selectedVariant.dataset_source ?? DatasetSource.GALAXY}
          driverOptions={driverDisplayOptions}
          excludeDimensions={[KpiDimension.REPORTING_CURRENCY]}
          renderMode='dimensionPicker'
          placeholderDrivers={placeholderDrivers}
          onChange={onDriverChange}
          onToggleSwitch={onDriverToggle}
        />
      </Box>
      {/* Column 3: Variant usage summary (across decks) */}
      <VariantUsageSummary
        usageSummary={usageSummary}
        isLoading={isUsageSummaryLoading}
        expandedDeckId={expandedDeckId}
        onExpandChange={onExpandChange}
      />
    </Box>
    {isDialogMode && <DialogActions>{editVariantActions}</DialogActions>}
  </>
);

export default EditVariantView;
